import React, { useState, useEffect } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Drawer, List, Button, Stack, useMediaQuery, Box, IconButton, Tooltip, Typography } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SchoolIcon from "@mui/icons-material/School";
import { logoutUser } from "../redux/slices/userSlice";
import msisLogoFull from "../utilities/dummy_assets/svgs/LOGO_MSIS_Light.svg";
import smallMSISLogo from "../utilities/dummy_assets/svgs/LOGO_MSIS_Light_Small.svg";
import expandIcon from "../utilities/dummy_assets/Expand_left_double.png";
import expandIconAlt from "../utilities/dummy_assets/Expand_left_double (1).png";
import mapItLogoSvg from "../utilities/dummy_assets/svgs/MapIt_Logo.svg";
import mapItLogoPng from "../utilities/dummy_assets/mapITlogo.png";

const iconBoxBaseStyles = {
  width: 40,
  height: 40,
  borderRadius: 2,
  display: "grid",
  placeItems: "center",
  transition: "background-color 0.25s ease",
  flexShrink: 0,
};

const Sidebar = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => (s?.user?.user ? s.user.user : {}));

  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("sidebarCollapsed");
      if (saved !== null) setCollapsed(saved === "true");
    } catch (e) { }
  }, []);

  const toggleCollapsed = () => {
    try {
      localStorage.setItem("sidebarCollapsed", (!collapsed).toString());
    } catch (e) { }
    setCollapsed((c) => !c);
  };

  const isPathActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const handleLogout = () => {
    try {
      dispatch(logoutUser());
    } catch (e) { }
    if (onClose) onClose();
    navigate("/login");
  };

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const collapsedWidth = 80;
  const expandedWidth = 180;
  const temporaryWidth = 200;
  const drawerWidth = isSmallScreen ? temporaryWidth : collapsed ? collapsedWidth : expandedWidth;

  const SidebarButton = ({ path, icon, title, onClick }) => {
    const active = path ? isPathActive(path) : false;
    const handleClick = () => {
      if (onClick) onClick();
      if (isSmallScreen && onClose) onClose();
    };

    const renderedIcon = React.isValidElement(icon)
      ? React.cloneElement(icon, { sx: { color: "white", fontSize: 18 } })
      : icon;

    return (
      <Tooltip title={title} placement="right">
        <Button
          component={path ? RouterLink : "button"}
          to={path || ""}
          variant="text"
          title={title}
          className={`sidebarButton ${active ? "activeSidebarButton" : ""}`}
          sx={{
            width: "100%",
            justifyContent: collapsed ? "center" : "flex-start",
            textTransform: "none",
            px: collapsed ? 0 : 1.25,
            py: 0.75,
            minHeight: 45,
            borderRadius: 2,
            gap: 0.75,
            color: "#273F77",
            backgroundColor: "transparent",
            '&:hover': { backgroundColor: "transparent" },
            '&:hover .sidebarIconBox': { backgroundColor: "#E79A2B" },
          }}
          onClick={handleClick}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={collapsed ? 0 : 1.25}
            sx={{ width: "100%", justifyContent: collapsed ? "center" : "flex-start" }}
          >
            <Box
              className="sidebarIconBox"
              sx={{
                ...iconBoxBaseStyles,
                backgroundColor: active ? "#E79A2B" : "#273F77",
              }}
            >
              {renderedIcon}
            </Box>
            {!collapsed && (
              <Typography variant="body2" sx={{ color: "#273F77", fontWeight: 600, fontSize: 14 }}>
                {title}
              </Typography>
            )}
          </Stack>
        </Button>
      </Tooltip>
    );
  };

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          transition: "0.5s ease-out",
          overflow: "hidden",
          backgroundColor: "#F2F3FC",
          border: "0px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100vh",
          padding: collapsed ? "0px 0" : "0px 8px",
        },
      }}
      variant={isSmallScreen ? "temporary" : "permanent"}
      anchor="left"
      open={isSmallScreen ? open : undefined}
      onClose={isSmallScreen ? onClose : undefined}
    >
      <div>
        <List className="drawer">
          <Stack direction="column" spacing={0} alignItems="stretch" justifyContent="center" sx={{ mt: 0 }}>
            <Box
              className="sidebarTop"
              sx={{
                mb: "10px !important",
                width: "100%",
                display: "flex",
                alignItems: "center",
                px: 0,
                height: 90,
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Stack direction="column" alignItems="center" justifyContent="center" sx={{ width: "100%", height: "100%" }}>
                <RouterLink to="/dashboard" style={{ display: "block", textAlign: "center", height: "100%", width: "100%" }}>
                  <img
                    src={collapsed ? smallMSISLogo : msisLogoFull}
                    alt="Logo"
                    style={{
                      height: collapsed ? '80px' : '85px',
                      width: 'auto',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </RouterLink>

                {!isSmallScreen && (
                  <IconButton
                    onClick={toggleCollapsed}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      bgcolor: "transparent",
                      border: "none",
                      boxShadow: "none",
                      "& img": { filter: "brightness(0.6)" },
                      "&:hover img": { filter: "brightness(0.95)" },
                      p: 0.5,
                    }}
                    title={collapsed ? "Expand" : "Collapse"}
                  >
                    <img src={collapsed ? expandIcon : expandIconAlt} alt={collapsed ? "expand" : "collapse"} style={{ height: 16, display: "block" }} />
                  </IconButton>
                )}
              </Stack>
            </Box>

            {user?.TL_name === "Org-Admin" ? (
              <>
                <SidebarButton path="/settings" icon={<SettingsOutlinedIcon />} title="Settings" />
                <SidebarButton icon={<LogoutOutlinedIcon />} title="Logout" onClick={handleLogout} />
              </>
            ) : (
              <>
                <SidebarButton path="/dashboard" icon={<DashboardOutlinedIcon />} title="Dashboard" />
                <SidebarButton path="/explore" icon={<LanguageOutlinedIcon />} title="Explore" />
                <SidebarButton path="/projects" icon={<FolderOpenOutlinedIcon />} title="Projects" />
                <SidebarButton path="/records" icon={<StoreOutlinedIcon />} title="Records" />
                <SidebarButton path="/training" icon={<SchoolIcon />} title="Training" />
                <SidebarButton icon={<LogoutOutlinedIcon />} title="Logout" onClick={handleLogout} />
              </>
            )}
          </Stack>
        </List>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "8px 0",
          marginBottom: "0px",
          boxSizing: "border-box",
        }}
      >
        <img
          src={collapsed ? mapItLogoPng : mapItLogoSvg}
          alt="Company Logo"
          style={{
            display: "block",
            margin: "0 auto",
            width: collapsed ? '35px' : '60px', // Set to 60px for expanded
            height: collapsed ? '35px' : '48.68px', // Set to 48.68px for expanded
            transition: "all 0.25s ease",
            objectFit: "contain",
          }}
        />
      </div>
    </Drawer>
  );
};

export default Sidebar;